import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useHistory } from "react-router-dom";
import dompurify from "isomorphic-dompurify";
import { CheckoutRoutes, standardCheckoutRoutes } from "@jti/fe-common";
import { DeliveryOptionType, loyalty, selectFirstAvailableShippingMethod, selectIsZeroValueOrder, selectOrderAttributes, selectOrderAttributesByTarget, selectTradeInOrderAttributes, setDeliveryStepData, useDispatch, useSelector, } from "@jti/store";
import { Form, MessageBox, MessageBoxVariant, useCountry, useForm, useTranslations } from "@jti/ui";
import WarningIcon from "@jti/ui/icons/information.svg";
import { OrderAttributes } from "../../components/OrderAttributes";
import { useAemModel } from "../../contexts";
import { DeliveryFormShippingOptions } from "../../forms/DeliveryForm/components/DeliveryFormShippingOptions";
import { useGtm } from "../../hooks/gtm/useGtm";
import useDelivery from "../../hooks/useDelivery";
import { useFreeTrial } from "../../hooks/useFreeTrial";
import { useGetProfile } from "../../hooks/useGetProfile";
import { LOYALTY_CHOICE, useLoyalty } from "../../hooks/useLoyalty";
import { useLoyaltyStatus } from "../../hooks/useLoyaltyStatus";
import { useMinimumPayableAmount } from "../../hooks/useMinimumPayableAmount";
import { useSubscription } from "../../hooks/useSubscription";
import { overrideOrderAttributesWithProfileExtra } from "../../utils/overrideOrderAttributesWithProfileExtra";
import { prepareConsentOrderAttributes, prepareOrderAttributes, prepareTradeInAttributes, } from "../../utils/prepareOrderAttributes";
import { DeliveryFormFooter } from "./components/DeliveryFormFooter";
import styles from "./DeliveryForm.module.scss";
const { sanitize } = dompurify;
export const DeliveryForm = ({ asideSubmitButtonWrapperRef }) => {
    const formRef = useRef(null);
    const history = useHistory();
    const dispatch = useDispatch();
    const { __ } = useTranslations();
    const { countryCode } = useCountry();
    const { isFreeTrial } = useFreeTrial();
    const { hasCampaignSelected } = useLoyalty();
    const minimumPayableAmount = useMinimumPayableAmount();
    const { isSubscription } = useSubscription();
    const { dispatchGtmShippingInfo } = useGtm();
    const { data: userProfileData } = useGetProfile();
    const { zeroValueOrder } = useAemModel();
    const freeTrialCart = useSelector((state) => state.freeTrialCart);
    const isZeroValueOrder = useSelector(selectIsZeroValueOrder(isFreeTrial));
    const cart = useSelector((state) => state.cart);
    const subtotalPrice = useSelector((state) => state.cart.prices.data?.subtotal.value);
    const firstAvailableShippingMethod = useSelector((state) => selectFirstAvailableShippingMethod(state, isFreeTrial));
    const loyaltyStatus = useLoyaltyStatus();
    const { shippingStepData } = useSelector((state) => state.standardCheckoutForm);
    const otherOrderAttributes = useSelector(selectOrderAttributes);
    const tradeInOrderAttributes = useSelector(selectTradeInOrderAttributes);
    const isConsentOnPage = useSelector((state) => selectOrderAttributesByTarget(state, "buttonPlaceOrder_before"));
    const { loyaltyChoice, loyaltyCampaignId } = useSelector((state) => state.loyalty);
    const { cartId, filteredShippingMethods, shippingMethods, getDefaultShippingMethods, setShippingMethods, placeOrderAction, hasOrderError, } = useDelivery();
    const isHomeDelivery = shippingStepData?.deliveryOptionType === DeliveryOptionType.AtHome;
    const form = useForm({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            shippingMethodCode: shippingStepData?.shippingMethodCode,
            orderAttributes: {},
        },
        shouldUnregister: false,
    });
    const { errors, isValid } = form.formState;
    const shippingMethodCodeInput = form.watch("shippingMethodCode");
    const method = useMemo(() => shippingMethods.data.find(({ methodCode }) => methodCode === shippingMethodCodeInput), [shippingMethods.data, shippingMethodCodeInput]);
    const isSubmitDisabled = (isHomeDelivery && !filteredShippingMethods.length) ||
        Object.keys(errors).length !== 0 ||
        !isValid ||
        !hasCampaignSelected ||
        (!isFreeTrial && minimumPayableAmount?.isReached);
    const handleMobileSubmit = () => {
        formRef.current?.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
    };
    const submitDeliveryForm = async (data) => {
        if (loyaltyStatus) {
            const validationStatus = loyaltyChoice === LOYALTY_CHOICE.REDEEM && !loyaltyCampaignId;
            dispatch(loyalty.actions.setLoyaltyError(validationStatus));
            if (validationStatus)
                return;
        }
        if (!cartId || !data.shippingMethodCode || !shippingStepData) {
            // TODO: Handle form submit error
            return;
        }
        const preparedConsentAttributes = prepareConsentOrderAttributes(data.orderAttributes);
        const preparedOrderAttributes = prepareOrderAttributes(otherOrderAttributes);
        const preparedTradeInAttributes = prepareTradeInAttributes(tradeInOrderAttributes);
        let additionalAttributes = [...preparedConsentAttributes, ...preparedOrderAttributes, ...preparedTradeInAttributes];
        const userProfileExtraAttributes = userProfileData?.extra;
        additionalAttributes = overrideOrderAttributesWithProfileExtra(additionalAttributes, userProfileExtraAttributes);
        const shippingMethod = filteredShippingMethods.find(({ methodCode }) => methodCode === data.shippingMethodCode);
        const placeOrderResult = await dispatch(placeOrderAction({
            cartId,
            additionalAttributes,
            isZeroValueOrder,
            shippingAddress: shippingStepData.shippingAddress,
        }));
        // placeOrder failed, action rejected with exception OrderExceptionCode.MagentoOrderCreationIssue and supported by the template
        if (!placeOrderResult.payload ||
            !("orderNumber" in placeOrderResult.payload) ||
            !("entityId" in placeOrderResult.payload)) {
            return;
        }
        const { entityId, orderNumber } = placeOrderResult.payload;
        // GTM tracking for shipping info to be triggered just once before the payment step
        const trackingData = isFreeTrial ? freeTrialCart : cart;
        shippingMethod && dispatchGtmShippingInfo(trackingData, shippingMethod);
        // Early return for zero value order
        if (isZeroValueOrder) {
            history.replace(standardCheckoutRoutes.order.getSummaryUrl(orderNumber));
            return;
        }
        dispatch(setDeliveryStepData({
            shippingMethodCode: data.shippingMethodCode,
            orderAttributes: data.orderAttributes,
            entityId,
            orderId: orderNumber,
        }));
        const checkoutRoutes = new CheckoutRoutes({ isFreeTrial });
        const paymentPageUrl = checkoutRoutes.payment.getBaseUrl(orderNumber);
        history.push(paymentPageUrl);
    };
    useEffect(function setInitialShippingMethodCode() {
        const firstAvailableShippingMethodCode = firstAvailableShippingMethod?.methodCode;
        if (!isHomeDelivery ||
            !firstAvailableShippingMethodCode ||
            firstAvailableShippingMethodCode === shippingMethodCodeInput) {
            return;
        }
        form.setValue("shippingMethodCode", firstAvailableShippingMethodCode);
    }, [firstAvailableShippingMethod]);
    useEffect(function fetchShippingMethods() {
        if (!cartId) {
            return;
        }
        dispatch(getDefaultShippingMethods({ cartId, countryCode }));
    }, [cartId, countryCode, cart.coupons, subtotalPrice, dispatch, getDefaultShippingMethods]);
    // TODO: Revisit this hook as it has unnecessary invokations which cause UI glitching
    useEffect(function setShippingMethodsOnCart() {
        if (shippingMethods.fetchStatus !== "success") {
            return;
        }
        const isShippingMethodsPayloadReady = !!(cartId && method?.methodCode && method?.carrierCode);
        if (!isShippingMethodsPayloadReady) {
            return;
        }
        if (isSubscription && method.disableForSubscription) {
            newrelic.noticeError(new Error("Selected shipping method (most probably, the only available) in subscription order, was disabled for subscription."));
            return;
        }
        dispatch(setShippingMethods({
            cartId,
            methodCode: method.methodCode,
            carrierCode: method.carrierCode,
        }));
    }, [
        cartId,
        shippingMethodCodeInput,
        cart.coupons,
        isSubscription,
        method?.methodCode,
        method?.carrierCode,
        shippingMethods.fetchStatus,
    ]);
    useEffect(() => {
        if (hasOrderError) {
            toast.error(__("cart.checkout.place_order.error"));
        }
    }, [hasOrderError]);
    return (_jsxs(Form, { "data-testid": "delivery-form", form: form, onSubmit: submitDeliveryForm, className: styles.container, ref: formRef, children: [isHomeDelivery && _jsx(DeliveryFormShippingOptions, {}), isZeroValueOrder && (_jsx(MessageBox, { isVisible: true, variant: MessageBoxVariant.message, icon: WarningIcon, children: _jsx("div", { "data-testid": "pay-on-delivery-message", dangerouslySetInnerHTML: {
                        __html: sanitize(zeroValueOrder?.userNotification),
                    } }) })), !isFreeTrial && minimumPayableAmount?.isReached && (_jsx(MessageBox, { isVisible: true, variant: MessageBoxVariant.warning, icon: WarningIcon, children: _jsx("p", { "data-testid": "minimum-payable-amount-reached", children: __("cart.order.message.minimum_payable_amount_reached") }) })), _jsxs("div", { "data-testid": "consents-section", children: [isConsentOnPage?.length > 0 && (_jsx("div", { "data-testid": "consents-title", className: styles.consentsTitle, children: __("cart.checkout.consents") })), _jsx(OrderAttributes, { target: "buttonPlaceOrder_before" })] }), _jsxs("div", { "data-testid": "place-order-button", children: [_jsx(DeliveryFormFooter, { isSubmitDisabled: isSubmitDisabled, className: styles.footerDesktop, "data-testid": "delivery-form-footer-desktop" }), asideSubmitButtonWrapperRef.current &&
                        createPortal(_jsx(DeliveryFormFooter, { onSubmitButtonClick: handleMobileSubmit, isSubmitDisabled: isSubmitDisabled, className: styles.footerMobile, "data-testid": "delivery-form-footer-mobile" }), asideSubmitButtonWrapperRef.current)] })] }));
};
//# sourceMappingURL=DeliveryForm.js.map